import {Injectable} from '@angular/core';
import {RequesterResponseModel} from '../../../services/models/requester.model';
import {NapiService} from '../../../services/napi.service';
import {RequesterService} from '../../../services/requester.service';
import {
  InteractionsCardItemDto,
  InteractionGroupDto,
  InteractionCardDto,
  InteractionDto,
  InteractionAnswerSaveDto,
  InteractionsCardsPageLayoutEnum, StatisticsDto, InteractionsCardCategoryDto
} from '../models/card.model';
import {TalkJSModel} from '../models/talkjs.model';
import {UserService} from '../../../services/user.service';


@Injectable({
  providedIn: 'root'
})
export class ConferenceService {
  constructor(
    private userService: UserService,
    private requesterService: RequesterService,
    private napiService: NapiService) {
  }

  getMyTalkJS(taskId: number): Promise<TalkJSModel> {
    return this.requesterService.get(`getMyTalkJS?task_id=${taskId}`)
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error getMyTalkJS >> ', e);
        return null;
      });
  }

  generateZoomAuth(meetingNumber, role): Promise<RequesterResponseModel> {
    return this.requesterService.post('generateZoomAuth', {meetingNumber, role})
      .then(data => {
        return data.result;
      })
      .catch(e => {
        console.log('Error generateZoomAuth >> ', e);
        return null;
      });
  }

  getActiveCards(interactionId: string): Promise<InteractionDto> {
    return this.napiService.get('interactions/getActiveCards/' + interactionId)
      .then(interaction => {
        console.log('getActiveCards', interaction);
        return this.extractInteraction(interaction);
      }).catch(e => {
        console.log('Error getActiveCards >> ', e);
        return null;
      });
  }


  getUserCards(interactionId: string, userId: string): Promise<InteractionDto> {
    return this.napiService.get('interactions/getUserCards/' + interactionId + '/' + userId)
      .then(interaction => {
        console.log('getUserCards', interaction);
        return this.extractInteraction(interaction);
      }).catch(e => {
        console.log('Error getActiveCards >> ', e);
        return null;
      });
  }

  extractInteraction(interaction) {
    if (!interaction) {
      return null;
    }

    return {
      id: interaction.id,
      name: interaction.name,
      clientId: interaction.clientId,
      isZoomEnabled: interaction.isZoomEnabled,
      pageLayout: InteractionsCardsPageLayoutEnum[interaction.pageLayout],
      createdAt: interaction.createdAt,
      updatedAt: interaction.updatedAt,
      interactionsGroups: this.extractInteractionsGroups(interaction.InteractionsGroups),
    } as InteractionDto;
  }

  extractInteractionsGroups(groups) {
    if (!groups || !groups.length) {
      return null;
    }

    return groups.map((group: any) => {
      return {
        id: group.id,
        interactionId: group.interactionId,
        name: group.name,
        order: group.order,
        score: group.score,
        status: group.status,
        isResults: group.isResults,
        createdAt: group.createdAt,
        updatedAt: group.updatedAt,
        interactionsCards: this.extractInteractionCards(group.InteractionsCards),
      } as InteractionGroupDto;
    }) as InteractionGroupDto[]
  }

  extractInteractionCards(items) {
    if (!items || !items.length) {
      return null;
    }

    return items.map((card: any) => {
      const translation = this.extractCardTranslation(card.InteractionsCardsTranslation);

      return {
        id: card.id,
        type: card.type,
        isTitle: card.isTitle,
        isText: card.isText,
        isRequired: card.isRequired,
        isMultiselect: card.isMultiselect,
        isRolePlay: card.isRolePlay,
        isNoCorrect: card.isNoCorrect,
        title: translation.title,
        text: translation.text,
        legend1: translation.legend1,
        legend2: translation.legend2,
        text1: translation.text1,
        text2: translation.text2,
        comment: translation.comment,
        url1: translation.url1,
        url2: translation.url2,
        minSelect: card.minSelect,
        maxSelect: card.maxSelect,
        ratingType: card.ratingType,
        score: card.score,
        order: card.order,
        color: card.styles.color,
        titleColor: card.styles.titleColor,
        backgroundColor: card.styles.backgroundColor,
        backgroundImage: card.styles.backgroundImage,
        backgroundOverlay: card.styles.backgroundOverlay,
        backgroundPosition: card.styles.backgroundPosition,
        backgroundSize: card.styles.backgroundSize,
        cardAnswer: this.extractInteractionAnswer(card.InteractionAnswer),
        interactionsCardCategories: this.extractCardCategories(card.InteractionCardCategory),
        interactionsCardItems: this.extractInteractionsCardItems(card.InteractionsCardItems, card.InteractionAnswer),
      } as InteractionCardDto;
    }) as InteractionCardDto[]
  }

  extractCardTranslation(translations) {
    if (!translations || !translations.length) {
      return null;
    }

    let translation = translations.find((t => t.langId === this.userService.langId));
    if (!translation) {
      translation = translations[0]; // Fallback to the first translation if no match found
    }

    return {
      id: translation.id,
      title: translation.title,
      text: translation.text,
      legend1: translation.legend1,
      legend2: translation.legend2,
      text1: translation.text1,
      text2: translation.text2,
      comment: translation.comment,
      url1: translation.url1,
      url2: translation.url2
    };
  }

  extractInteractionsCardItems(items, answers) {
    if (!items || !items.length) {
      return null;
    }

    return items.map((item: any) => {
      const translation = this.extractCardItemTranslation(item.InteractionsCardItemsTranslation);

      return {
        id: item.id,
        isValid: item.isValid,
        score: item.score,
        order: item.order,
        correctOrder: item.correctOrder,
        interactionCardCategoryId: item.interactionCardCategoryId,
        text: translation.text,
        textAnswer: translation.textAnswer,
        comment: translation.comment,
        cardItemAnswer: this.extractInteractionAnswerItem(answers, item.id)
      } as InteractionsCardItemDto
    });
  }

  extractCardItemTranslation(translations) {
    if (!translations || !translations.length) {
      return null;
    }

    let translation = translations.find((t => t.langId === this.userService.langId));
    if (!translation) {
      translation = translations[0]; // Fallback to the first translation if no match found
    }

    return {
      id: translation.id,
      text: translation.text,
      textAnswer: translation.textAnswer,
      comment: translation.comment
    };
  }

  extractCardCategories(categories) {
    if (!categories || !categories.length) {
      return null;
    }

    return categories.map((category: any) => {
      const translation = this.extractCardCategoryTranslation(category.InteractionCardCategoryTranslation);

      return {
        id: category.id,
        interactionsCardId: category.interactionsCardId,
        isText: category.isText,
        title: translation.title,
        text: translation.text,
        order: category.order,
      } as InteractionsCardCategoryDto
    });
  }

  extractCardCategoryTranslation(translations) {
    if (!translations || !translations.length) {
      return null;
    }

    let translation = translations.find((t => t.langId === this.userService.langId));
    if (!translation) {
      translation = translations[0]; // Fallback to the first translation if no match found
    }

    return {
      id: translation.id,
      title: translation.title,
      text: translation.text
    };
  }

  extractInteractionAnswer(answers) {
    if (!answers || !answers.length) {
      return null;
    }

    return {
      id: answers[0].id,
      interactionCardId: answers[0].interactionCardId,
      score: answers[0].score,
      isCompleted: answers[0].isCompleted,
      isPassed: answers[0].isPassed,
      groupExtId: answers[0].groupExtId,
      userId: answers[0].userId,
      formAnswer: answers[0].InteractionAnswerForm,
      createdAt: answers[0].createdAt,
      updatedAt: answers[0].updatedAt,
    }
  }

  extractInteractionAnswerItem(answers, itemId) {
    if (!answers || !answers.length) {
      return null;
    }

    return answers[0].InteractionAnswerItems.find((answer: any) => answer.interactionsCardItemId === itemId)
  }

  saveAnswer(answer: InteractionAnswerSaveDto): Promise<any> {
    return this.napiService.post('interaction-cards/saveAnswer/', answer);
  }

  getStatistics(interactionCardId: string, userGroupExtId: number): Promise<StatisticsDto> {
    return this.napiService.get(`interaction-cards/stat/${interactionCardId}/${userGroupExtId}`);
  }

  getStatisticsExt(interactionCardId: string, userGroupExtId: number): Promise<StatisticsDto> {
    return this.napiService.get(`interaction-cards/statExt/${interactionCardId}/${userGroupExtId}`);
  }

  getStatisticsForm(interactionCardId: string, userGroupExtId: number): Promise<StatisticsDto> {
    return this.napiService.get(`interaction-cards/statForm/${interactionCardId}/${userGroupExtId}`);
  }
}
